import {
  BookabilityStatus,
  Occupancy,
  Price
} from '../../../../types/searchAvailability'

/**
 * The "compare-at" price — the number we strike through — per ADR-040 §2.3/§2.5.
 *
 * Returns the amount only when it is STRICTLY ABOVE the price actually shown;
 * otherwise null (render no strikethrough). Two cases collapse into that rule:
 * equal (nothing to advertise) and below (a struck price under the charged price
 * reads as a discount while actually being an increase).
 *
 * `!==` is deliberately NOT used: it passes when the anchor is *lower* than the
 * shown price, which is exactly the defect ADR-040 exists to remove.
 * `beforeDiscount` may be absent once the producer omits it, so presence is
 * checked before comparing.
 *
 * No currency check is needed here, and its absence is deliberate: both figures
 * come from the same `Price` object and therefore share its single `currency`
 * field. ADR-040 §2.5's cross-currency precondition applies to comparing
 * separate `rateBreakouts[]` entries, which each carry their own currency — not
 * to this pair.
 */
export function compareAtTotal(price: Price): number | null {
  const anchor = price?.beforeDiscount?.totalPrice
  const shown = price?.afterDiscount?.totalPrice

  if (
    anchor == null ||
    shown == null ||
    !Number.isFinite(anchor) ||
    !Number.isFinite(shown)
  ) {
    return null
  }

  return anchor > shown ? anchor : null
}

/**
 * Whole-percent discount off the compare-at price, or null when there is no
 * honest anchor to compute against. Guards the denominator: an absent or zero
 * anchor would otherwise yield NaN/Infinity in the badge.
 */
export function discountPercent(price: Price): number | null {
  const anchor = compareAtTotal(price)
  const shown = price?.afterDiscount?.totalPrice

  if (
    anchor == null ||
    shown == null ||
    !Number.isFinite(shown) ||
    anchor <= 0 ||
    shown < 0
  ) {
    return null
  }

  return Math.round((1 - shown / anchor) * 100)
}

export function getBookabilityLabel(
  status: BookabilityStatus,
  occupancy: Occupancy
): string {
  switch (status) {
    case 'BOOKABLE':
      return 'Bookable'
    case 'NO_AVAILIBILITY':
      return 'No Availability'
    case 'RESTRICTIONS':
      return 'Unavailable due to restrictions'
    case 'NO_PRICE':
      return `No available rate for ${
        occupancy.adults + occupancy.extraBeds
      } adults`
    case 'NO_PAYMENTTERM':
      return 'No Payment Term'
    default:
      return status // fallback, but ideally never hit
  }
}
